import { useSelector } from "react-redux"
import { useAppDispatch } from '../../redux/store'
import './account.scss'
import { accountSelector, setUserInfo } from '../../redux/account/accountSlice'
import { EditProfileForm } from '../../Components/Forms/EditProfileForm/EditProfileForm'
import { ChangePasswordForm } from '../../Components/Forms/ChangePasswordForm/ChangePasswordForm'
import { useState } from "react"
import { Button, Col, Container, Row, Modal, Alert } from "react-bootstrap"
import timeService from "../../services/time/timeService"
import useUploadImage from "../../Hooks/UseUploadImage/useUploadImageHook"
import { useChangePasswordMutation, useUpdateProfileMutation } from "../../services/bClientApi"
import QuickForm from "../../Components/Forms/QuickForm/QuickForm"
import ProfileInformation from "../../Components/Users/ProfileInformation"
import TimezoneSettings from "../../Components/Users/TimezoneSettings"
import NotificationSettings from "../../Components/Users/NotificationSettings"

export default function AccountPage() {


  const [
    updateProfile,
    {
      error: updateProfileError,
      isLoading: updateProfileLoading
    }
  ] = useUpdateProfileMutation()

  const [
    changePassword,
    {
      error: changePasswordError,
      isLoading: changePasswordLoading
    }
  ] = useChangePasswordMutation()

  const {
    username,
    firstName,
    lastName,
    profilePic,
    phone,
    role,
    lastLogin,
    notificationSettings = {
      whatsApp: false,
      email: false,
      sms: false
    }
  } = useSelector(accountSelector)
  const dispatch = useAppDispatch()

  const {
    uploadImageFn,
    isLoading: uploadImageLoading,
    // error: uploadImageError,
  } = useUploadImage()

  const [editProfileModal, setEditProfileModal] = useState(false)
  const [editPasswordModal, setEditPasswordModal] = useState(false)
  const [editNotificationModal, setEditNotificationModal] = useState(false)
  const [updateCount, setUpdateCount] = useState(0)

  const handleProfileSubmit = async (updatedProfile: {
    firstName?: string,
    lastName?: string,
    profilePic?: string,
    phone?: string
  }) => {
    await updateProfile({
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      phone: updatedProfile.phone,
      profilePic: updatedProfile.profilePic,
    }).unwrap()
    dispatch(setUserInfo({
      firstName: updatedProfile.firstName,
      lastName: updatedProfile.lastName,
      phone: updatedProfile.phone,
      profilePic: updatedProfile.profilePic
    }))
    setEditProfileModal(false)
    return
  }

  const handleNotificationSubmit = async (notificationSettings: { whatsApp: boolean, email: boolean, sms: boolean }) => {
    await updateProfile({
      notificationSettings
    }).unwrap()
    dispatch(setUserInfo({
      notificationSettings
    }))
    setEditNotificationModal(false)
  }

  const handlePasswordSubmit = async (passwordInfo: { oldPassword: string, newPassword: string }) => {
    await changePassword(passwordInfo).unwrap()
    setEditPasswordModal(false)
  }

  return (
    <Container>
      <Row>
        <Col>
          <h1 className="mb-4">Profile Page</h1>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>

          {updateProfileError && (
            <Alert variant="danger" className="mb-3">
              <strong>Profile Update Error:</strong> {JSON.stringify(updateProfileError)}
            </Alert>
          )}
          {changePasswordError && (
            <Alert variant="danger" className="mb-3">
              <strong>Password Change Error:</strong> {JSON.stringify(changePasswordError)}
            </Alert>
          )}

          {/* Profile Information Card */}
          <ProfileInformation
            username={username}
            firstName={firstName}
            lastName={lastName}
            profilePic={profilePic}
            phone={phone}
            role={role}
            lastLogin={lastLogin}
          />

          {/* Timezone Settings Card */}
          <TimezoneSettings
            key={updateCount}
            onTimezoneRefresh={() => {
              timeService.updateActiveTimeZone(true)
              setUpdateCount(updateCount + 1)
            }}
          />

          {/* Notification Settings Card */}
          <NotificationSettings
            notificationSettings={notificationSettings}
          />

          {/* Action Buttons */}
          <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
            <Button
              variant="primary"
              onClick={() => setEditProfileModal(true)}
            >
              Edit Profile
            </Button>

            <Button
              variant="info"
              onClick={() => setEditNotificationModal(true)}
            >
              Edit Notifications
            </Button>

            <Button
              variant="warning"
              onClick={() => setEditPasswordModal(true)}
            >
              Change Password
            </Button>
          </div>
        </Col>
      </Row>

      {/* Edit Profile Modal */}
      <Modal show={editProfileModal} onHide={() => setEditProfileModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Profile</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <EditProfileForm
            firstName={firstName}
            lastName={lastName}
            profilePic={profilePic}
            phone={phone}
            disabled={updateProfileLoading || uploadImageLoading}
            onSubmit={handleProfileSubmit}
            uploadImageFn={uploadImageFn}
            onCancel={() => setEditProfileModal(false)} 
          />
        </Modal.Body>
      </Modal>

      {/* Edit Notifications Modal */}
      <Modal show={editNotificationModal} onHide={() => setEditNotificationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Notification Settings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <QuickForm
            initialData={notificationSettings}
            fields={[
              {
                fieldName: 'whatsApp',
                fieldLabel: 'WhatsApp',
                fieldType: 'checkbox',
                disabled: !phone || phone.length === 0
              },
              {
                fieldName: 'email',
                fieldLabel: 'Email',
                fieldType: 'checkbox',
              },
              {
                fieldName: 'sms',
                fieldLabel: 'SMS',
                fieldType: 'checkbox',
                disabled: !phone || phone.length === 0
              },
            ]}
            onSubmit={handleNotificationSubmit}
            onCancel={() => setEditNotificationModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Change Password Modal */}
      <Modal show={editPasswordModal} onHide={() => setEditPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ChangePasswordForm
            onSubmit={handlePasswordSubmit}
            disabled={changePasswordLoading}
            onCancel={() => setEditPasswordModal(false)}
          />
        </Modal.Body>
      </Modal>
    </Container>
  )
}
